// components/Chatbot/ExpenseChatbot.js
import React, { useState, useRef, useEffect } from "react";

const CHATBOT_API_URL = "https://expense-chart-w33t.vercel.app/api/chatbot";

function renderMarkdown(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:700">$1</strong>')
    .replace(/\*([^*]+?)\*/g, "<em>$1</em>")
    .replace(
      /^(\d+)\.\s(.+)$/gm,
      '<div style="display:flex;gap:8px;margin:2px 0"><span style="color:#f97316;font-weight:700;min-width:16px">$1.</span><span>$2</span></div>'
    )
    .replace(
      /^[-•]\s(.+)$/gm,
      '<div style="display:flex;gap:8px;margin:2px 0"><span style="color:#64748b">•</span><span>$1</span></div>'
    )
    .replace(/\n/g, "<br/>");
}


const QUICK_PROMPTS = [
  "What is my current balance?",
  "Top spending categories?",
  "How much did I spend this month?",
  "Show recent transactions",
];

export default function ExpenseChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
      "Hi! I'm your expense assistant 💰 Ask me anything about your spending, balance, or trends.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorBar, setErrorBar] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getUserData = () => {
    try {
      const stored = localStorage.getItem("userData");
      if (stored) return JSON.parse(stored);
    } catch { }
    return {};
  };
  
  const adjustTextareaHeight = () => {
    const el = inputRef.current;
    if (!el) return;
  
    el.style.height = "auto"; // reset
    el.style.height = Math.min(el.scrollHeight, 120) + "px"; // max 120px
  };
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen]);

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    const userData = getUserData();
    const userId = userData.userId;
    const groupId = userData.groupId;

    if (!userId) {
      alert("Please log in to use the chatbot.");
      return;
    }

    setErrorBar(null);
    const newMessages = [...messages, { role: "user", content: userText }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    const history = newMessages.slice(1, -1).slice(-16);

    try {
      const res = await fetch(CHATBOT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          groupId,
          message: userText,
          conversationHistory: history,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const errMsg = data?.error || `Error (${res.status})`;
        setErrorBar(errMsg);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Sorry, something went wrong: ${errMsg}` },
        ]);
        return;
      }

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply || "No response received." },
      ]);
    } catch (err) {
      console.error("Fetch error:", err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Network error. Please check your connection and try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showQuickPrompts = messages.length <= 1;

  return (
    <>
      {/* ── Floating Button ── */}
      <button
        onClick={() => setIsOpen((o) => !o)}
        title="Expense Assistant"
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          width: "56px",
          height: "56px",
          borderRadius: "50%",
          background: "#2563eb",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 24px rgba(37,99,235,0.40)",
          zIndex: 9999,
          transition: "transform 0.15s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {isOpen ? (
          /* X icon */
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" /><path d="m6 6 12 12" />
          </svg>
        ) : (
          /* Chat bubble icon */
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
            <path d="M8 12h.01" /><path d="M12 12h.01" /><path d="M16 12h.01" />
          </svg>
        )}
      </button>

      {/* ── Chat Window ── */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            bottom: "96px",
            right: "24px",
            width: "340px",
            maxHeight: "520px",
            display: "flex",
            flexDirection: "column",
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)",
            zIndex: 9998,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              background: "#2563eb",
              color: "#fff",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexShrink: 0,
            }}
          >
            {/* Avatar circle with chatbot image */}
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "#fff",
                overflow: "hidden",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="https://res.cloudinary.com/dmyq2ymj9/image/upload/v1776247153/chatbot_sxwnvy.png"
                alt="Assistant"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  // Fallback to emoji if image missing
                  e.currentTarget.style.display = "none";
                  e.currentTarget.parentNode.innerHTML = '<span style="font-size:18px">💰</span>';
                }}
              />
            </div>

            {/* Title + online dot */}
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontWeight: 600, fontSize: "14px", lineHeight: 1.2 }}>
                Expense Assistant
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#4ade80",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.80)" }}>
                  Online
                </span>
              </div>
            </div>
          </div>

          {/* ── Error bar ── */}
          {errorBar && (
            <div
              style={{
                background: "#fef2f2",
                color: "#dc2626",
                fontSize: "12px",
                padding: "6px 16px",
                borderBottom: "1px solid #fecaca",
                flexShrink: 0,
              }}
            >
              ⚠️ {errorBar}
            </div>
          )}

          {/* ── Messages ── */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              background: "#f9fafb",
            }}
          >
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    maxWidth: "80%",
                    padding: "9px 13px",
                    fontSize: "13px",
                    lineHeight: 1.55,
                    borderRadius:
                      msg.role === "user"
                        ? "18px 18px 4px 18px"
                        : "18px 18px 18px 4px",
                    background: msg.role === "user" ? "#2563eb" : "#ffffff",
                    color: msg.role === "user" ? "#ffffff" : "#1f2937",
                    border: msg.role === "user" ? "none" : "1px solid #e5e7eb",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.role === "assistant" ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "18px 18px 18px 4px",
                    padding: "10px 14px",
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background: "#9ca3af",
                        display: "inline-block",
                        animation: `chatDot 1.2s ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* ── Quick Prompts ── */}
          {showQuickPrompts && (
            <div
              style={{
                padding: "10px 14px 12px",
                background: "#f9fafb",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                flexShrink: 0,
              }}
            >
              {QUICK_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => sendMessage(p)}
                  style={{
                    alignSelf: "flex-start",
                    background: "transparent",
                    color: "#3b82f6",
                    border: "1px solid #3b82f6",
                    borderRadius: "999px",
                    padding: "5px 14px",
                    fontSize: "12px",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "background 0.12s",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#eff6ff")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* ── Input ── */}
          <div
            style={{
              padding: "10px 12px",
              borderTop: "1px solid #e5e7eb",
              background: "#ffffff",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                border: "1px solid #d1d5db",
                borderRadius: "25px",
                padding: "6px 6px 6px 14px",
                background: "#f9fafb",
              }}
            >
              <textarea
                ref={inputRef}
                rows={1}
                maxLength={500}
                placeholder="Write a message..."
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  adjustTextareaHeight();
                }}
                onKeyDown={handleKeyDown}
                disabled={loading}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  fontSize: "13px",
                  color: "#111827",
                  resize: "none",
                  fontFamily: "inherit",
                  lineHeight: 1.5,
                  padding: 0,
                  overflowY: "auto",   // ✅ important
                  maxHeight: "120px",
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  border: "none",
                  background: loading || !input.trim() ? "#d1d5db" : "#2563eb",
                  color: "#fff",
                  cursor: loading || !input.trim() ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "background 0.15s",
                }}
              >
                {/* Paper plane icon */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Typing animation */}
      <style>{`
        @keyframes chatDot {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </>
  );
}